'use client';

import { useSuspenseQuery } from '@apollo/client/react';
import { FC, PropsWithChildren, useMemo } from 'react';

import { GetPublicationsByUserIdQuery } from '@/.gql/graphql';
import { GET_PUBLICATIONS_BY_USER_ID } from '@/.graphql';

import { createCustomContext } from './context';

interface IPublicationsByUserProps {
  publications: GetPublicationsByUserIdQuery['getPublicationsByUserId']['data'];
  hasMorePublications: boolean;
  isLoadingPublications: boolean;
  loadMorePublicationsByUsers: () => void;
}

const [PublicationsByUserContext, usePublicationsByUserContext] =
  createCustomContext<IPublicationsByUserProps>({
    name: 'PublicationsByUserContext',
    strict: false,
  });

export { usePublicationsByUserContext };

interface Props extends PropsWithChildren {
  userId: string;
}

export const PublicationsByUserProvider: FC<Props> = ({ children, userId }) => {
  const {
    data: imageData,
    fetchMore: fetchMorePublicationsByUsers,
    networkStatus: imageNetworkStatus,
  } = useSuspenseQuery(GET_PUBLICATIONS_BY_USER_ID, {
    variables: {
      cursor: null,
      userId,
    },
  });

  const publications = useMemo(
    () => imageData?.getPublicationsByUserId?.data || [],
    [imageData],
  );
  // const hasMorePublications = useMemo(
  //   () => imageData?.getPublicationsByUserId.nextCursor || false,
  //   [imageData],
  // );
  const hasMorePublications = !!imageData?.getPublicationsByUserId.nextCursor;

  const isLoadingPublications = imageNetworkStatus === 3;
  const loadMorePublicationsByUsers = useMemo(
    () => () => {
      if (!hasMorePublications || isLoadingPublications) {
        return;
      }

      return fetchMorePublicationsByUsers({
        variables: {
          cursor: imageData?.getPublicationsByUserId.nextCursor,
        },
      });
    },
    [
      hasMorePublications,
      isLoadingPublications,
      imageData,
      fetchMorePublicationsByUsers,
    ],
  );

  const contextValue = useMemo(
    () => ({
      publications,
      hasMorePublications,
      loadMorePublicationsByUsers,
      isLoadingPublications,
    }),
    [
      publications,
      hasMorePublications,
      loadMorePublicationsByUsers,
      isLoadingPublications,
    ],
  );

  return (
    <PublicationsByUserContext value={contextValue}>
      {children}
    </PublicationsByUserContext>
  );
};
